import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MenuIcon,
  ScanIcon,
  SectionIcon,
  SignOutIcon,
  UserIcon,
} from '../../components/ui/icons';
import { ErrorBoundary } from '../../components/ui/ErrorBoundary';
import { ScanProvider } from '../../platform/scanning';
import { BackupBanner } from '../admin/BackupBanner';
import { useAuth } from '../auth/AuthContext';
import { useResolveAndOpen } from '../scan/useOpenScanned';
import { FooterMeta } from './FooterMeta';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher } from './ThemeSwitcher';
import { UpdateNotice } from './UpdateNotice';
import { NAV_GROUPS, PHONE_ITEMS, type NavItem } from './sections';
import { NavigationProvider, type Destination, type Navigation } from './navigation';

const COLLAPSED_KEY = 'storageapp.nav.collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Application shell: a grouped side panel on a desk, a bottom bar on a phone,
 * and the current screen between them.
 *
 * It also owns cross-section navigation, because it is the only place that
 * knows what a section is — see `navigation.tsx`.
 */
export function AppShell() {
  const { t } = useTranslation();
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawer, setDrawer] = useState(false);

  // A phone's drawer is a modal thing: arriving somewhere is the end of using it.
  useEffect(() => setDrawer(false), [location.pathname, location.search]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // The panel still collapses; it just will not be remembered.
    }
  }, [collapsed]);

  const goTo = useCallback(
    (destination: Destination, id: string, entityType?: string) => {
      switch (destination) {
        case 'component':
          navigate(`/components/${id}`);
          return;
        case 'location':
          navigate(`/locations/${id}`);
          return;
        case 'purchase':
          navigate(`/purchases/${id}`);
          return;
        case 'project':
          navigate(`/projects/${id}`);
          return;
        case 'spec':
          navigate(`/specs/${id}`);
          return;
        case 'history':
          navigate(
            `/audit?entity_type=${encodeURIComponent(entityType ?? '')}` +
              `&entity_id=${encodeURIComponent(id)}`,
          );
      }
    },
    [navigate],
  );

  const navigation = useMemo<Navigation>(
    () => ({
      open: goTo,
      // Orders are behind a permission; without it the chip stays a plain label
      // rather than a link that answers with 403.
      canOpen: (destination: Destination) => {
        if (destination === 'purchase') return can('purchase.write');
        if (destination === 'history') return can('audit.read');
        return true;
      },
    }),
    [goTo, can],
  );

  // The scanner key on a desk. A wired gun is held in one hand and the other is
  // on the keyboard, so reaching for the mouse to start a scan is the one step
  // worth removing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'F2') return;
      event.preventDefault();
      navigate('/scan');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // Where a scan goes when no field on the screen has claimed it: look the code
  // up and open whatever it names, from wherever the person happens to be.
  const resolveAndOpen = useResolveAndOpen();

  const allowed = (item: NavItem) => !item.permission || can(item.permission);

  const needsSecondFactor = Boolean(user?.mfa_required && !user.mfa_enrolled);

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(allowed),
  })).filter((group) => group.items.length > 0);

  const link = (item: NavItem) => (
    <NavLink
      key={item.key}
      to={item.path}
      className={({ isActive }) => (isActive ? 'nav__link is-active' : 'nav__link')}
      // The tooltip is what a collapsed panel has instead of a word.
      title={t(`nav.${item.key}`)}
    >
      <SectionIcon name={item.key} className="nav__icon" />
      <span className="nav__label">{t(`nav.${item.key}`)}</span>
    </NavLink>
  );

  const panel = (
    <nav className="nav">
      {groups.map((group) => (
        <div className="nav__group" key={group.key}>
          <p className="nav__group-title">{t(`nav.group.${group.key}`)}</p>
          {group.items.map(link)}
        </div>
      ))}
    </nav>
  );

  return (
    <NavigationProvider value={navigation}>
      <ScanProvider onFallback={resolveAndOpen}>
      <div className={collapsed ? 'shell shell--collapsed' : 'shell'}>
        <aside className="sidebar">
          <div className="sidebar__brand">
            <strong>{t('app.name')}</strong>
            <button
              type="button"
              className="sidebar__collapse"
              onClick={() => setCollapsed((was) => !was)}
              aria-label={t(collapsed ? 'nav.expand' : 'nav.collapse')}
              title={t(collapsed ? 'nav.expand' : 'nav.collapse')}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </button>
          </div>

          {panel}

          <div className="sidebar__footer">
            {/* The name is the way into one's own account rather than a label:
                a second factor is set up once, from wherever you happen to be,
                and hunting for it in a menu is how it never gets set up. */}
            <NavLink
              to="/security"
              title={user?.display_name}
              className={({ isActive }) =>
                [
                  'nav__link',
                  isActive ? 'is-active' : '',
                  // Collapsed there is no room for the "set it up" chip, and a
                  // second factor nobody is reminded about is one nobody sets up.
                  needsSecondFactor ? 'nav__link--attention' : '',
                ]
                  .filter(Boolean)
                  .join(' ')
              }
            >
              <UserIcon className="nav__icon" />
              <span className="nav__label">
                {user?.display_name}
                {needsSecondFactor && <span className="badge badge--warn">{t('security.setUp')}</span>}
              </span>
            </NavLink>
            <ThemeSwitcher />
            <LanguageSwitcher />
            <button
              type="button"
              className="nav__link sidebar__out"
              title={t('auth.signOut')}
              onClick={() => void signOut()}
            >
              <SignOutIcon className="nav__icon" />
              <span className="nav__label">{t('auth.signOut')}</span>
            </button>
            <FooterMeta />
          </div>
        </aside>

        <div className="shell__body">
          {/* Above the content rather than on a page of its own: an alarm nobody
              passes is an alarm nobody reads. */}
          {can('backup.manage') && <BackupBanner onOpen={() => navigate('/backups')} />}
          {/* Here rather than on the login screen: an update prompt in front of
              somebody who cannot get in yet is a prompt in the way. */}
          <UpdateNotice />

          <main className="shell__main">
            {/* Reset on the address: walking to another section is how someone
                gets out of a screen that has just fallen over. */}
            <ErrorBoundary resetKey={location.pathname + location.search}>
              <Outlet />
            </ErrorBoundary>
          </main>

          {/* Scanning is an action, not a place, and a whole bar spent on one
              button is a bar's worth of screen not spent on the catalogue. It
              floats over the content instead, the way a "back to top" does. On a
              phone it would sit on top of the bottom bar, which already has it. */}
          <button
            type="button"
            className="scan-fab"
            onClick={() => navigate('/scan')}
            title={`${t('nav.scan')} · F2`}
            aria-label={t('nav.scan')}
          >
            <ScanIcon />
          </button>

          {/* Mark above word, and the word is the short one: "Места хранения"
              across a fifth of a phone wrapped onto two lines and had its
              second line cut off by the edge of the screen. */}
          <nav className="bottombar">
            {PHONE_ITEMS.filter(allowed).map((item) => (
              <NavLink
                key={item.key}
                to={item.path}
                className={({ isActive }) => (isActive ? 'bottombar__tab is-active' : 'bottombar__tab')}
              >
                <SectionIcon name={item.key} />
                <span>{t(`nav.short.${item.key}`)}</span>
              </NavLink>
            ))}
            <NavLink
              to="/scan"
              className={({ isActive }) =>
                isActive ? 'bottombar__tab bottombar__scan is-active' : 'bottombar__tab bottombar__scan'
              }
            >
              <ScanIcon />
              <span>{t('nav.short.scan')}</span>
            </NavLink>
            <button type="button" className="bottombar__tab" onClick={() => setDrawer(true)}>
              <MenuIcon />
              <span>{t('nav.more')}</span>
            </button>
          </nav>
        </div>

        {drawer && (
          <div className="drawer">
            <button
              type="button"
              className="drawer__backdrop"
              aria-label={t('common.close')}
              onClick={() => setDrawer(false)}
            />
            <div className="drawer__sheet">
              {panel}
              <div className="sidebar__footer">
                <NavLink to="/security" className="nav__link">
                  <UserIcon className="nav__icon" />
                  <span className="nav__label">{user?.display_name}</span>
                </NavLink>
                <ThemeSwitcher />
                <LanguageSwitcher />
                <button
                  type="button"
                  className="nav__link sidebar__out"
                  onClick={() => void signOut()}
                >
                  <SignOutIcon className="nav__icon" />
                  <span className="nav__label">{t('auth.signOut')}</span>
                </button>
                {/* The phone has no side panel, and this sheet is the whole of
                    its menu — leaving these out of it means the one place they
                    are ever needed is the one place they are missing. */}
                <FooterMeta />
              </div>
            </div>
          </div>
        )}
      </div>
      </ScanProvider>
    </NavigationProvider>
  );
}
